import { useMemo } from 'react'
import { request } from '../api/client'
import { useResource } from '../hooks/useResource'
import type { OperatorRole, Permission } from './permissions'

export interface MyAccess {
  role: string
  operatorRole: OperatorRole | null
  permissions: Permission[]
}

export const getMyAccess = (token: string, signal?: AbortSignal): Promise<MyAccess> =>
  request<MyAccess>('/auth/me/permissions', { context: 'user', token, signal })

export interface Access {
  /** True only once the server has answered and granted every permission. */
  can: (...needed: Permission[]) => boolean
  operatorRole: OperatorRole | null
  loading: boolean
}

/**
 * What the signed-in person may do, from `GET /auth/me/permissions`.
 *
 * ⚠️ WHILE LOADING, `can` IS FALSE — a control appears once it is known to be
 * allowed, rather than flashing in and then vanishing. The server refuses the
 * action either way.
 */
export function useAccess(token: string | undefined): Access {
  const res = useResource<MyAccess>(
    (signal) => getMyAccess(token ?? '', signal),
    ['my-access', token ?? ''],
    { enabled: Boolean(token) },
  )
  return useMemo(() => {
    const held = new Set(res.data?.permissions ?? [])
    return {
      can: (...needed: Permission[]) => res.data !== null && needed.every((p) => held.has(p)),
      operatorRole: res.data?.operatorRole ?? null,
      loading: res.state === 'loading',
    }
  }, [res.data, res.state])
}
